import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Copy, MailCheck, MailWarning, RefreshCw, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function RecordRow({ record, index }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(record.value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }
  return (
    <tr data-testid={`email-dns-record-${index}`}>
      <td>{record.type}</td>
      <td><code>{record.name}</code></td>
      <td className="email-dns-value"><code>{record.value}</code>{record.priority ? <small> priority {record.priority}</small> : null}</td>
      <td><span className={`email-dns-state email-dns-${record.status}`}>{record.status.replaceAll("_", " ")}</span></td>
      <td>
        <button className="button button-secondary" data-testid={`email-dns-copy-${index}`} type="button" onClick={copy}>
          <Copy size={13} /> {copied ? "Copied" : "Copy"}
        </button>
      </td>
    </tr>
  );
}

export const EmailDeliveryStatus = () => {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [test, setTest] = useState({ sending: false, result: null });

  const sendTest = useCallback(async () => {
    setTest({ sending: true, result: null });
    try {
      const response = await api.post("/team/email-status/test");
      setTest({ sending: false, result: response.data });
    } catch (requestError) {
      setTest({ sending: false, result: { sent: false, error: formatApiError(requestError) } });
    }
  }, []);

  const load = useCallback(async (verify = false) => {
    setChecking(true);
    try {
      const response = verify ? await api.post("/team/email-status/verify") : await api.get("/team/email-status");
      setState(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p className="form-message error-message" data-testid="email-status-error">{error}</p>;
  if (!state) return null;
  const pending = !state.verified;
  return (
    <section className={`email-delivery-card ${pending ? "email-delivery-pending" : "email-delivery-ok"}`} data-testid="email-delivery-status">
      <div className="email-delivery-heading">
        {pending ? <MailWarning size={18} /> : <MailCheck size={18} />}
        <div className="email-delivery-copy">
          <strong data-testid="email-delivery-state">
            {pending ? "Email delivery paused — sender domain not verified" : "Email delivery active"}
          </strong>
          <span>
            Sending as {state.sender || "not configured"}
            {pending && state.status !== "domain_missing" && ` · add the DNS records below at your ${state.domain} host, then click Verify`}
            {state.status === "domain_missing" && ` · ${state.domain} is not registered with the email provider`}
          </span>
        </div>
        <div className="email-delivery-actions">
          <button className="button button-secondary" data-testid="email-status-verify-button" disabled={checking} type="button" onClick={() => load(true)}>
            <RefreshCw size={14} className={checking ? "spin" : ""} /> {checking ? "Checking…" : pending ? "Verify now" : "Re-check"}
          </button>
          {!pending && (
            <button className="button button-secondary" data-testid="email-status-send-test-button" disabled={test.sending} type="button" onClick={sendTest}>
              <Send size={14} /> {test.sending ? "Sending…" : "Send test email"}
            </button>
          )}
          {state.records.length > 0 && (
            <button className="button button-secondary" data-testid="email-status-toggle-records" type="button" onClick={() => setOpen((value) => !value)}>
              DNS records <ChevronDown size={14} className={open ? "sidebar-chevron-open" : ""} />
            </button>
          )}
        </div>
      </div>
      {test.result && (
        <p className={`form-message ${test.result.sent ? "team-email-status team-email-sent" : "error-message"}`} data-testid="email-test-result">
          {test.result.sent ? `Test email sent to ${test.result.recipient} from ${test.result.sender}.` : `Test email failed: ${test.result.error}`}
        </p>
      )}
      {open && (
        <table className="email-dns-table" data-testid="email-dns-table">
          <thead>
            <tr><th>Type</th><th>Host / name</th><th>Value</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {state.records.map((record, index) => <RecordRow key={`${record.type}-${record.name}`} record={record} index={index} />)}
          </tbody>
        </table>
      )}
    </section>
  );
};
